/**
 * CouponRedeemSection — Shared component used in both Wallet page and KYC Portal (Profile tab).
 * Uses GET/POST /api/partner/redeem-coupon for status + redemption.
 * No hardcoded coupon codes or amounts — all data comes from backend.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Gift, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * @param {function} onRedeemed - optional callback fired after successful redemption
 * @param {string}   className  - extra CSS classes for outer wrapper
 */
export function CouponRedeemSection({ onRedeemed, className = '' }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [status, setStatus] = useState(null); // { already_redeemed, amount_credited, coupon_code, redeemed_at }
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/partner/redeem-coupon`);
      setStatus(res.data);
    } catch {
      setStatus({ already_redeemed: false });
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleRedeem = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Please enter a coupon code');
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const res = await axios.post(`${API}/partner/redeem-coupon`, { coupon_code: trimmed });
      setSuccessMsg(res.data.message);
      setStatus({
        already_redeemed: true,
        amount_credited: res.data.amount_credited,
        coupon_code: trimmed.toUpperCase(),
        redeemed_at: new Date().toISOString(),
      });
      // Notify DashboardLayout + KYCPortal to refresh wallet balance immediately
      window.dispatchEvent(new CustomEvent('cloudsetu:wallet-updated'));
      if (onRedeemed) onRedeemed(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to redeem coupon. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (statusLoading) return null;

  const alreadyRedeemed = status?.already_redeemed;

  return (
    <div
      className={`bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden ${className}`}
      data-testid="coupon-redeem-section"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-amber-100 bg-gradient-to-r from-amber-50 to-orange-50 flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
          <Gift className="h-4 w-4 text-amber-600" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-800 text-sm">Redeem Coupon</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {alreadyRedeemed ? 'Coupon already applied to your wallet' : 'Enter your coupon code to credit your wallet instantly'}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-5">
        {alreadyRedeemed ? (
          <div
            className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3"
            data-testid="coupon-already-redeemed"
          >
            <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-green-700">Coupon redeemed successfully.</p>
              {status.amount_credited != null && (
                <p className="text-xs text-green-600 mt-0.5">
                  ₹{Number(status.amount_credited).toLocaleString('en-IN')} credited to your wallet
                  {status.coupon_code ? ` via code ${status.coupon_code}` : ''}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {successMsg && (
              <div
                className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3"
                data-testid="coupon-success-msg"
              >
                <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                <p className="text-sm font-semibold text-green-700">{successMsg}</p>
              </div>
            )}
            <div className="flex gap-2">
              <Input
                value={code}
                onChange={e => { setCode(e.target.value); setError(''); }}
                placeholder="Enter coupon code"
                className="h-11 font-mono tracking-widest uppercase"
                onKeyDown={e => e.key === 'Enter' && handleRedeem()}
                data-testid="coupon-code-input"
              />
              <Button
                onClick={handleRedeem}
                disabled={loading || !code.trim()}
                className="h-11 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-5 shrink-0"
                data-testid="coupon-redeem-btn"
              >
                {loading ? 'Redeeming...' : 'Redeem'}
              </Button>
            </div>
            {error && (
              <p className="text-sm text-red-600" data-testid="coupon-error">
                {error}
              </p>
            )}
            <ul>
              <li className="text-xs text-slate-400">Coupon codes are case-insensitive. Each partner can redeem only once.</li>
              <li className="text-xs text-slate-400">Please check your registered email ID.</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
